<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="850px"
    scrollable
  >
    <v-card v-if="account" style="max-height: 85vh" class="d-flex flex-column">
      <v-card-title
        class="headline primary white--text d-flex justify-space-between align-center"
      >
        <div>
          <v-icon left dark>mdi-format-list-bulleted-square</v-icon>
          Tilitapahtumat: {{ account.accountNumber }}
          <span
            v-if="account.subAccount"
            class="text-caption grey--text text--lighten-2 ml-1 font-mono"
          >
            ({{ account.subAccount }})
          </span>
          {{ account.accountName }}
        </div>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4 flex-grow-1" style="overflow-y: auto">
        <!-- Account overview header -->
        <v-row dense class="mb-3">
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center grey lighten-4">
              <div class="text-caption grey--text">Budjetoitu (Arvio)</div>
              <div class="text-subtitle-1 font-weight-bold">
                {{ formatCurrency(account.arvio) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center grey lighten-4">
              <div class="text-caption grey--text">Pohjatoteutuma</div>
              <div class="text-subtitle-1 font-weight-bold">
                {{ formatCurrency(account.baseToteutuma) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center grey lighten-4">
              <div class="text-caption grey--text">Tuodut tapahtumat</div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                {{ formatCurrency(transactionsSum) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center grey lighten-4">
              <div class="text-caption grey--text">Yhteensä toteutuma</div>
              <div class="text-subtitle-1 font-weight-bold font-weight-black">
                {{ formatCurrency(totalToteutuma) }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-subtitle-1 font-weight-bold mb-2">
          Kohdistetut tositteet ({{ accountTransactions.length }} kpl):
        </div>

        <div
          v-if="accountTransactions.length === 0"
          class="text-center py-6 grey--text"
        >
          <v-icon size="48" color="grey lighten-2"
            >mdi-receipt-text-outline</v-icon
          >
          <div class="mt-2">Ei tuotuja tapahtumia tällä tilillä.</div>
        </div>

        <v-card
          v-for="tx in accountTransactions"
          :key="tx.id"
          outlined
          class="mb-3"
        >
          <v-card-text class="pa-3">
            <div
              class="d-flex justify-space-between align-center flex-wrap mb-1"
            >
              <div>
                <v-chip x-small color="primary" dark class="mr-2">{{
                  tx.tositelaji
                }}</v-chip>
                <v-chip
                  v-if="tx.isSplit"
                  x-small
                  color="deep-purple"
                  text-color="white"
                  class="mr-2 font-weight-medium"
                >
                  <v-icon x-small left>mdi-call-split</v-icon>
                  Jaettu tosite ({{ tx.splitIndex }}/{{ tx.splitCount }})
                </v-chip>
                <span class="font-weight-bold mr-2">#{{ tx.tositenro }}</span>
                <span class="grey--text text-caption mr-3">{{
                  tx.tositepvm
                }}</span>
                <span class="font-weight-medium mr-2">{{
                  tx.partnerName
                }}</span>
                <span
                  v-if="tx.isSplit && tx.originalTotalAmount"
                  class="grey--text text-caption"
                >
                  (Koko tosite: {{ formatCurrency(tx.originalTotalAmount) }})
                </span>
              </div>
              <div
                class="font-weight-bold text-subtitle-2"
                :class="tx.amount > 0 ? 'green--text' : 'red--text'"
              >
                {{ formatCurrency(tx.amount) }}
              </div>
            </div>

            <!-- Vientiselitteet -->
            <div
              class="mt-2 pa-2 rounded grey lighten-4"
              v-if="tx.items && tx.items.length > 0"
            >
              <div
                v-for="(it, itIdx) in tx.items"
                :key="itIdx"
                class="d-flex justify-space-between align-center text-caption py-1 border-bottom"
              >
                <div>
                  <v-icon x-small color="grey">mdi-circle-small</v-icon>
                  <span>{{ it.vientiselite || "(Ei vientiselitettä)" }}</span>
                  <span v-if="it.alvPercent" class="grey--text ml-1"
                    >(ALV {{ it.alvPercent }})</span
                  >
                </div>
                <span class="font-weight-bold">{{
                  formatCurrency(it.amount)
                }}</span>
              </div>
            </div>

            <!-- Reassign / Move action -->
            <div v-if="canManage" class="d-flex justify-end align-center mt-2">
              <v-btn
                text
                x-small
                color="secondary"
                @click="openMoveDialog(tx)"
                class="mr-2"
              >
                <v-icon x-small left>mdi-swap-horizontal</v-icon>
                Siirrä toiselle tilille
              </v-btn>
              <v-btn
                text
                x-small
                color="error"
                @click="confirmRemoveTransaction(tx)"
              >
                <v-icon x-small left>mdi-delete</v-icon>
                Poista kohdistus
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Sulje</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Sub-dialog: Move Transaction to another Account -->
    <v-dialog v-model="moveDialog" max-width="500px" persistent>
      <v-card v-if="txToMove">
        <v-card-title class="headline primary white--text">
          Siirrä tosite toiselle tilille
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3 text-body-2">
            Tosite:
            <strong>{{ txToMove.tositelaji }} #{{ txToMove.tositenro }}</strong>
            ({{ txToMove.partnerName }})<br />
            Summa: <strong>{{ formatCurrency(txToMove.amount) }}</strong>
          </div>

          <v-select
            v-model="targetProjectId"
            :items="projectItems"
            item-text="fullName"
            item-value="id"
            label="Kustannuspaikka"
            outlined
            dense
          ></v-select>

          <v-autocomplete
            v-model="targetAccountId"
            :items="getAccountsForTargetProject"
            item-text="displayName"
            item-value="id"
            label="Uusi budjettitili"
            outlined
            dense
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="moveDialog = false">Peruuta</v-btn>
          <v-btn
            color="primary"
            :disabled="!targetAccountId"
            :loading="moving"
            @click="executeMove"
          >
            Siirrä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { updateTransaction, deleteTransaction } from "@/services/budgetService";

export default {
  name: "BudgetAccountTransactionsDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    account: {
      type: Object,
      default: null,
    },
    projectId: {
      type: String,
      default: "",
    },
    transactions: {
      type: Array,
      default: () => [],
    },
    projects: {
      type: Array,
      default: () => [],
    },
    canManage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      moveDialog: false,
      txToMove: null,
      targetProjectId: "",
      targetAccountId: "",
      moving: false,
    };
  },
  computed: {
    accountTransactions() {
      if (!this.account) return [];
      return this.transactions.filter(
        (tx) => tx.accountId === this.account.id && tx.status === "assigned",
      );
    },
    transactionsSum() {
      return this.accountTransactions.reduce((acc, tx) => {
        let amt = parseFloat(tx.amount) || 0;
        if (
          amt > 0 &&
          (tx.tositelaji === "Ostolasku" ||
            tx.tositelaji === "Kululasku" ||
            this.account?.type === "expense")
        ) {
          amt = -amt;
        }
        return acc + amt;
      }, 0);
    },
    totalToteutuma() {
      const base = parseFloat(this.account?.baseToteutuma) || 0;
      return base + this.transactionsSum;
    },
    projectItems() {
      return this.projects.map((p) => ({
        id: p.id || p.code,
        fullName: p.fullName || `${p.code} ${p.name}`,
      }));
    },
    getAccountsForTargetProject() {
      const proj = this.projects.find(
        (p) =>
          String(p.id) === String(this.targetProjectId) ||
          String(p.code) === String(this.targetProjectId),
      );
      if (!proj || !proj.accounts) return [];

      return proj.accounts
        .filter(
          (a) =>
            !a.isTotal &&
            !a.isTitle &&
            !a.isHeader &&
            (!a.accountNumber ||
              !/^\d{1,3}$/.test(String(a.accountNumber).trim())),
        )
        .map((a) => ({
          ...a,
          displayName: `${a.accountNumber ? a.accountNumber + " " : ""}${
            a.accountName
          }`,
        }));
    },
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0,00 €";
      return (
        Number(val).toLocaleString("fi-FI", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + " €"
      );
    },
    openMoveDialog(tx) {
      this.txToMove = tx;
      this.targetProjectId = tx.projectId || this.projectId;
      this.targetAccountId = "";
      this.moveDialog = true;
    },
    async executeMove() {
      if (!this.txToMove || !this.targetAccountId) return;
      this.moving = true;
      try {
        const accs = this.getAccountsForTargetProject;
        const targetAcc = accs.find((a) => a.id === this.targetAccountId);
        const isTargetExpense =
          targetAcc &&
          (targetAcc.type === "expense" ||
            (targetAcc.type === "investment" &&
              targetAcc.accountNumber &&
              String(targetAcc.accountNumber).startsWith("7")));
        const isTargetIncome = targetAcc && targetAcc.type === "income";

        let newAmount = parseFloat(this.txToMove.amount) || 0;
        if (isTargetExpense && newAmount > 0) {
          newAmount = -Math.abs(newAmount);
        } else if (isTargetIncome && newAmount < 0) {
          newAmount = Math.abs(newAmount);
        }

        await updateTransaction(
          this.txToMove.id,
          {
            projectId: this.targetProjectId,
            accountId: this.targetAccountId,
            accountNumber: targetAcc ? targetAcc.accountNumber : "",
            accountName: targetAcc ? targetAcc.accountName : "",
            amount: newAmount,
          },
          {
            actionType: "TRANSACTION_UPDATE",
            summary: `Tosite #${this.txToMove.tositenro} siirretty tilille ${
              targetAcc?.accountNumber || ""
            } ${targetAcc?.accountName || ""}`,
            details: `Kustannuspaikka: ${
              this.targetProjectId
            }, summa: ${this.formatCurrency(newAmount)}`,
          },
        );

        this.$toast?.success?.("Tosite siirretty onnistuneesti!");
        this.moveDialog = false;
        this.txToMove = null;
      } catch (err) {
        console.error("Error moving transaction:", err);
        this.$toast?.error?.("Siirto epäonnistui");
      } finally {
        this.moving = false;
      }
    },
    async confirmRemoveTransaction(tx) {
      if (
        confirm(
          `Haluatko varmasti poistaa kohdistuksen tositteelta #${tx.tositenro}? Tosite palaa hylätyksi/kohdistamattomaksi.`,
        )
      ) {
        try {
          await updateTransaction(
            tx.id,
            {
              status: "declined",
              declineReason: "Kohdistus poistettu käyttäjän toimesta",
            },
            {
              actionType: "TRANSACTION_UPDATE",
              summary: `Tositteen #${tx.tositenro} kohdistus poistettu`,
              details: `Tosite palautettu hylätyksi tililtä ${
                this.account?.accountName || ""
              }`,
            },
          );
          this.$toast?.success?.("Kohdistus poistettu!");
        } catch (err) {
          console.error("Error unassigning transaction:", err);
          this.$toast?.error?.("Poisto epäonnistui");
        }
      }
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
}
</style>
